import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const OAuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(true);

  useEffect(() => {
    const handleCallback = async () => {
      const token = searchParams.get('token');

      if (!token) {
        setError('No authentication token received. Please try again.');
        setProcessing(false);
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      try {
        localStorage.setItem('access_token', token);

        const response = await fetch('/user/profile', {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch user profile');
        }

        const userData = await response.json();
        
        // Add role property for ProtectedRoute compatibility
        userData.role = userData.role_name;
        
        updateUser(userData);

        const roleName = userData.role_name;
        
        setTimeout(() => {
          switch (roleName) {
            case 'customer':
              navigate('/customer-dashboard');
              break;
            case 'rider':
              if (userData.approval_status === 'pending' || userData.approval_status === 'rejected') {
                navigate('/pending-approval');
              } else {
                navigate('/rider-dashboard');
              }
              break;
            case 'merchant':
              if (userData.approval_status === 'pending' || userData.approval_status === 'rejected') {
                navigate('/pending-approval');
              } else {
                navigate('/merchant-dashboard');
              }
              break;
            case 'admin':
              navigate('/admin-dashboard');
              break;
            default:
              navigate('/');
          }
        }, 1000);
        
      } catch (err: any) {
        console.error('OAuth callback error:', err);
        setError('Authentication failed. Please try logging in again.');
        setProcessing(false);
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    handleCallback();
  }, [searchParams, navigate, updateUser]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-sky-50 via-white to-blue-50">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-volexx-lg shadow-volexx-lg border border-gray-100 p-8">
          <div className="text-center">
            {processing && !error && (
              <>
                <div className="mb-6">
                  <div className="mx-auto w-16 h-16 relative">
                    <div className="absolute inset-0 border-4 border-volexx-blue-200 rounded-full animate-ping"></div>
                    <div className="absolute inset-0 border-4 border-volexx-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Completing Sign In
                </h2>
                <p className="text-gray-600">
                  Please wait while we verify your account...
                </p>
              </>
            )}
            
            {error && (
              <>
                <div className="mb-6">
                  <svg className="mx-auto h-16 w-16 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Authentication Failed
                </h2>
                <p className="text-gray-600 mb-4">
                  {error}
                </p>
                <p className="text-sm text-gray-500">
                  Redirecting to login page...
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OAuthCallback;
